/**
 * Admin Backup Export.
 *
 * Downloads a full ZIP backup (database.sql + JSON-per-table + uploads/) so
 * the site can be migrated to a new version. The endpoint is streamed, so we
 * fetch as a blob and prompt the browser to save it.
 */
import { useState } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const FORMATS = [
  { kind: 'sql',     label: 'database.sql',     desc: 'Full pg_dump — restore with `psql -f database.sql`.' },
  { kind: 'json',    label: 'data/<Table>.json', desc: 'One JSON file per Sequelize model — portable across schema changes.' },
  { kind: 'uploads', label: 'uploads/',         desc: 'Every file in the backend uploads directory (product images, etc.).' },
  { kind: 'meta',    label: 'manifest.json',    desc: 'Export timestamp, row counts, restore notes.' },
];

export default function BackupExport() {
  const [downloading, setDownloading] = useState(false);
  const [lastResult, setLastResult]   = useState(null);

  const download = async () => {
    setDownloading(true);
    setLastResult(null);
    try {
      const res = await api.get('/admin/backup/export', { responseType: 'blob' });

      // Filename comes from the server's Content-Disposition header.
      const cd = res.headers['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/.exec(cd);
      const filename = match ? match[1] : `aloha-amino-backup-${new Date().toISOString().slice(0, 10)}.zip`;

      const blob = new Blob([res.data], { type: 'application/zip' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setLastResult({
        filename,
        sizeBytes: blob.size,
        at: new Date(),
      });
      toast.success('Backup downloaded');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Download failed';
      toast.error(`Backup failed: ${msg}`);
    } finally {
      setDownloading(false);
    }
  };

  const fmtBytes = (n) => {
    if (!n) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
    return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-2">Server Backup</h1>
      <p className="text-gray-500 mb-8">
        Download every row of data and every uploaded file, packaged for migration to a new
        version of Aloha Amino.
      </p>

      <div className="bg-white rounded-xl border p-6 shadow-sm mb-6">
        <h2 className="text-lg font-semibold mb-4">What's included</h2>
        <ul className="space-y-3">
          {FORMATS.map(f => (
            <li key={f.kind} className="flex items-start gap-3">
              <span className="mt-1 inline-block w-2 h-2 rounded-full bg-tea-600 flex-shrink-0" />
              <div>
                <div className="font-mono text-sm font-semibold text-gray-900">{f.label}</div>
                <div className="text-sm text-gray-500">{f.desc}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-xl border p-6 shadow-sm mb-6">
        <h2 className="text-lg font-semibold mb-2">Download backup</h2>
        <p className="text-sm text-gray-500 mb-4">
          The server builds the archive on demand and streams it to your browser. Larger
          datasets can take a moment — keep this tab open until the file appears in your
          downloads.
        </p>
        <button
          type="button"
          onClick={download}
          disabled={downloading}
          className="px-5 py-2.5 bg-tea-600 hover:bg-tea-700 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors"
        >
          {downloading ? 'Preparing backup…' : 'Download full backup (.zip)'}
        </button>

        {lastResult && (
          <div className="mt-5 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            <div className="font-semibold text-blue-800">Last download</div>
            <div className="text-blue-700 mt-1">
              <code className="font-mono">{lastResult.filename}</code>
              {' · '}{fmtBytes(lastResult.sizeBytes)}
              {' · '}{lastResult.at.toLocaleString()}
            </div>
          </div>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900">
        <div className="font-semibold mb-1">Heads up</div>
        <ul className="list-disc list-inside space-y-1">
          <li>The archive contains everything in the database — including hashed passwords, refresh tokens, and Stripe IDs. Treat it like a secret.</li>
          <li>Secrets in <code className="font-mono">.env</code> (JWT secret, SMTP password, Stripe keys) are <strong>not</strong> included. Re-enter them on the new server.</li>
          <li>To restore: create a fresh Postgres database, run <code className="font-mono">psql -d &lt;db&gt; -f database.sql</code>, then copy <code className="font-mono">uploads/</code> into <code className="font-mono">backend/uploads/</code> on the destination.</li>
        </ul>
      </div>
    </div>
  );
}
